import store from '@/store'
import { sanitizeCardDescription } from '@/utils/sanitize'
import workflowApi from '@/api/workflowApi'

export const workflowHelpers = {
  methods: {
    getSystemGroupedBy (rootEntity, entityCard) {
      if (!entityCard) {
        return null
      }

      return {
        type: rootEntity ? 'linked-entity' : 'unlinked-text',
        rootEntity,
        text: rootEntity ? rootEntity.name : sanitizeCardDescription(entityCard.description || ''),
        boundedContext: rootEntity ? rootEntity.boundedContext : entityCard?.boundedContext
      }
    },
    convertLLMResponseToEntity (entityFromChat) {
      const entityDataFields = entityFromChat.dataFields.map(field => {
        return {
          name: field.fieldName,
          cardinality: 'one-to-one',
          exampleData: field.exampleData,
          primaryKey: !!field.primaryKey,
          tags: field.tags
        }
      })
      return ({
        name: entityFromChat.name,
        type: 'Dimension',
        dataFields: entityDataFields
      })
    },
    async deleteRequirementTypes (requirementTypes) {
      await Promise.all(requirementTypes.map(async (rt) => {
        return await this.$store.dispatch('requirementTypes/deleteRequirementType', { id: rt.id, workflowId: this.workflow.id })
      }))
    },
    async createNewRequirementTypes (requirementTypes) {
      return await Promise.all(requirementTypes.map(async (requirementType) => {
        const input = {
          title: requirementType.title,
          description: requirementType.title,
          color: requirementType.color,
          showInBacklog: requirementType.showInBacklog,
          triggerType: requirementType.triggerType,
          aggregateType: requirementType.aggregateType,
          defaultType: requirementType.defaultType,
          order: requirementType.order
        }

        const newRequirementType = await this.$store.dispatch('requirementTypes/createRequirementType', {
          workflowId: this.workflow.id, input
        })

        return {
          oldId: requirementType.id,
          newId: newRequirementType.id
        }
      }))
    },
    async initializeWorkflow (workflow, fileName, newRTIds, newBCIds, newBoundedContexts) {
      const newRequirementJsons = []

      workflow.eventsJson.forEach((event) => {
        if (!Array.isArray(event.requirementsJson) || !event.requirementsJson.length) return

        event.requirementsJson.forEach((requirement) => {
          const matchingType = newRTIds.find(rt => rt.oldId === requirement.cardTypeId)
          if (matchingType) {
            requirement.cardTypeId = matchingType.newId
          }
          requirement.creatorId = this.user.id
          requirement.eventId = event.id
          requirement.boundedContext = newBCIds.find(bc => bc.oldId === requirement.boundedContext)?.newId
          newRequirementJsons.push(requirement)
        })
        event.requirementsJson = []
        event.laneIndex = workflow.lanes.findIndex(lane => lane.id === event.laneId)
        delete event.version
      })

      await workflowApi.updateWorkflowMutation(
        this.workflow.projectId,
        {
          id: this.workflow.id,
          projectId: this.workflow.projectId,
          eventsJson: [],
          lanes: workflow.lanes,
          groups: workflow.groups,
          expectedVersion: this.workflow.version,
          dataModels: workflow.dataModels,
          updateNote: {
            userRealName: this.user.name,
            action: 'imported workflow',
            target: fileName
          },
          boundedContexts: newBoundedContexts
        }
      )

      return {
        eventsJson: workflow.eventsJson,
        newRequirementJsons
      }
    },
    async addEvents (eventsJson) {
      const eventMap = new Map()
      const rootEvents = []
      const notConnectedEvents = []
      const addedEvents = []

      eventsJson.forEach(event => {
        if (!eventMap.has(event.parents[0])) {
          eventMap.set(event.parents[0], [])
        }
        eventMap.get(event.parents[0]).push(event)

        if (event.parents[0] === 'start') {
          rootEvents.push(event) // Root events go first
        }
      })

      // BFS algorithm to update events in correct order
      const queue = [...rootEvents]

      while (queue.length > 0) {
        const currentEvent = queue.shift()

        currentEvent.index = this.workflow.eventsJson?.length > 0 ? -1 : -2
        currentEvent.laneIndex = this.workflow.lanes.findIndex(lane => lane.id === currentEvent.laneId)
        currentEvent.expectedVersion = currentEvent.version
        delete currentEvent.version

        const missingEvents = currentEvent.parents.filter(parentId => parentId !== 'start' && !addedEvents.includes(parentId))
        if (missingEvents.length) {
          notConnectedEvents.push(JSON.parse(JSON.stringify(currentEvent)))
        }
        currentEvent.parents = currentEvent.parents.filter(parentId => !missingEvents.includes(parentId))

        const eventsJson = [currentEvent]
        for (const parentId of currentEvent?.parents || []) {
          const index = this.workflow.eventsJson.findIndex(ev => ev.id === parentId)
          if (index >= 0) {
            eventsJson.push({
              id: this.workflow.eventsJson[index].id,
              expectedVersion: this.workflow.eventsJson[index].version,
              index
            })
          }
        }

        await workflowApi.updateWorkflowMutation(
          this.workflow.projectId,
          {
            id: this.workflow.id,
            projectId: this.workflow.projectId,
            eventsJson,
            expectedVersion: this.workflow.version,
            updateNote: {
              userRealName: this.user.name,
              action: 'added event',
              target: currentEvent.description
            }
          }
        )

        addedEvents.push(currentEvent.id)

        // Enqueue child events
        if (eventMap.has(currentEvent.id)) {
          queue.push(...eventMap.get(currentEvent.id))
        }
      }

      // Complete connections for not fully connected events
      for (const currentEvent of notConnectedEvents) {
        const curIndex = this.workflow.eventsJson.findIndex(ev => ev.id === currentEvent.id)

        const updateEventsJson = [{
          expectedVersion: this.workflow.eventsJson[curIndex].version,
          id: currentEvent.id,
          index: curIndex,
          parents: currentEvent.parents
        }]
        for (const parentId of currentEvent?.parents || []) {
          const index = this.workflow.eventsJson.findIndex(ev => ev.id === parentId)
          if (index >= 0) {
            updateEventsJson.push({
              id: this.workflow.eventsJson[index].id,
              expectedVersion: this.workflow.eventsJson[index].version,
              index
            })
          }
        }

        await workflowApi.updateWorkflowMutation(
          this.workflow.projectId,
          {
            id: this.workflow.id,
            projectId: this.workflow.projectId,
            eventsJson: updateEventsJson,
            expectedVersion: this.workflow.version,
            updateNote: {
              userRealName: this.user.name,
              action: 'updated event',
              target: currentEvent.description
            }
          }
        )
      }
    },
    async addRequirementsToEvent (newRequirementJsons) {
      for (const rt of newRequirementJsons) {
        const newRequirement = await workflowApi.createRequirementJson(this.workflow.id, rt.eventId, rt)
        if (!newRequirement.requirements.length) {
          return
        }

        await Promise.all(rt.comments.map(async (comment) => {
          return await this.$store.dispatch('workflow/addCardReply', {
            workflowId: this.workflow.id,
            requirementId: newRequirement.requirements[0].id,
            input: {
              text: comment.text,
              generatedByAi: comment.generatedByAi
            }
          })
        }))
        await Promise.all(rt.reactions.map(async (reaction) => {
          return await this.$store.dispatch('workflow/toggleCardReaction', {
            workflowId: this.workflow.id,
            input: {
              requirementId: newRequirement.requirements[0].id,
              direction: reaction.direction
            }
          })
        }))
      }
    },
    async addBoundedContexts (boundedContexts, newBCIds) {
      for (const bcId of newBCIds) {
        if (boundedContexts[bcId.oldId]) {
          for (const bcObject of boundedContexts[bcId.oldId]) {
            await this.$store.dispatch('boundedContexts/createObject', {
              boundedContextId: bcId.newId,
              contents: bcObject.contents,
              filepath: bcObject.filepath,
              prompt: bcObject.prompt
            })
          }
        }
      }
    }
  },
  computed: {
    workflowStatusCompleted () {
      return this.workflow?.status === 2 || store.state.payments.subscriptionAccessType !== 'FULL'
    }
  }
}
